import type { Modifier, Modifiers, SubstitutionModifier } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export function generateSubstitutionsAndUpdateOthers(params: Params): Modifiers {
  const { additions, removals, isUpsellPricingEnabled } = params;

  const initialState: Modifiers = {
    additions: [],
    removals: [...removals],
    substitutions: [],
  };

  return isUpsellPricingEnabled
    ? additions.reduce(generateSubstitutionsByGroup, initialState)
    : additions.reduce(generateSubstitutions, initialState);
}

// ─────────────────────────────────────────────────────────────────────────────

function generateSubstitutions(changes: Modifiers, addition: Modifier) {
  const { additions, removals, substitutions } = changes;
  const { subKind, modificationGroupId } = addition;

  if (!subKind) {
    return changes;
  }

  const maybeRemoval = getSameSubKindRemoval(subKind, modificationGroupId, removals);

  // NOTE: If there is no removal of the same kind, then there is no substitution.
  if (!maybeRemoval) {
    return { ...changes, additions: [...additions, addition] };
  }

  // Otherwise we can proceed by converting the corresponding removal to a substitution.

  const { removalIndex, removal } = maybeRemoval;

  return {
    ...changes,
    removals: withoutModification(removals, removalIndex),
    substitutions: withSubstitution(substitutions, addition, removal),
  };
}

function getSameSubKindRemoval(subKind: string, modificationGroupId: string, removals: Modifier[]) {
  const maybeSameKindRemovalIndex = removals.findIndex(
    (removal) => removal.modificationGroupId === modificationGroupId && removal.subKind === subKind,
  );

  const maybeSameKindRemoval = removals[maybeSameKindRemovalIndex];

  if (maybeSameKindRemovalIndex === -1) {
    return null;
  }

  return {
    removalIndex: maybeSameKindRemovalIndex,
    removal: maybeSameKindRemoval,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function generateSubstitutionsByGroup(changes: Modifiers, addition: Modifier) {
  const { additions, removals, substitutions } = changes;
  const { modificationGroupId } = addition;

  const removalIndex = removals.findIndex(
    (removal) => removal.modificationGroupId === modificationGroupId,
  );

  if (removalIndex === -1) {
    return { ...changes, additions: [...additions, addition] };
  }

  return {
    ...changes,
    removals: withoutModification(removals, removalIndex),
    substitutions: withSubstitution(substitutions, addition, removals[removalIndex]),
  };
}

// ─── Shared ─────────────────────────────────────────────────────────────────

function withoutModification(modificationChanges: Modifier[], targetIndex: number) {
  return modificationChanges.filter((_, index) => index !== targetIndex);
}

function withSubstitution(
  substitutions: SubstitutionModifier[],
  addition: Modifier,
  removal: Modifier,
) {
  const substitution: SubstitutionModifier = {
    subKind: addition.subKind,
    addedIngredientId: addition.ingredientId,
    removedIngredientId: removal.ingredientId,
    additionCost: addition.additionCost,
    removalCost: removal.removalCost,
    substitutionCost: addition.substitutionCost,
    modificationGroupId: addition.modificationGroupId,
  };

  return [...substitutions, substitution];
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  additions: Modifier[];
  removals: Modifier[];
  isUpsellPricingEnabled?: boolean;
};
